'use client';

import React, { useState, useEffect } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { CustomButton } from '@/components/ui/custom-button';

interface Stage {
  id: number;
  title: string;
  videoSrc: string;
  alt: string;
}

const stages: Stage[] = [
  {
    id: 1,
    title: 'Understand',
    alt: 'Student understanding the content',
    videoSrc: '/carousel_recordings/understand.gif'
  },
  {
    id: 2,
    title: 'Retain',
    alt: 'Student using Spaced Repetition to retain the content',
    videoSrc: '/carousel_recordings/retain.gif'
  },
  {
    id: 3,
    title: 'Apply',
    alt: 'Student applying the content via tests',
    videoSrc: '/carousel_recordings/apply.gif'
  },
  {
    id: 4,
    title: 'Improve',
    alt: 'Professors improving their student learning outcomes via the insights report',
    videoSrc: '/carousel_recordings/insights_report.png'
  }
];

const LearningStagesCarousel = () => {
  const [activeStage, setActiveStage] = useState(0);

  const handleKeyDown = (e: React.KeyboardEvent, index: number) => {
    if (e.key === 'Enter' || e.key === ' ') {
      e.preventDefault();
      setActiveStage(index);
    } else if (e.key === 'ArrowLeft' && index > 0) {
      setActiveStage(index - 1);
    } else if (e.key === 'ArrowRight' && index < stages.length - 1) {
      setActiveStage(index + 1);
    }
  };

  // Auto-advance carousel every 8 seconds
  useEffect(() => {
    const interval = setInterval(() => {
      setActiveStage((prev) => {
        const next = prev + 1;
        return next >= stages.length ? 0 : next;
      });
    }, 8000);

    return () => clearInterval(interval);
  }, []);

  return (
    <div className="w-full my-20 md:my-36 lg:mt-[350px]">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        {/* Stage Navigation */}
        <div className="flex flex-col items-center mb-6 md:mb-8">
          <h2 className="text-3xl md:text-4xl lg:text-5xl font-bold text-gray-900 mb-6 text-center">
            Make your LMS Truly Adaptive
          </h2>
          
          <div className="w-full sm:w-fit mx-auto">
            <nav 
              className="grid grid-cols-2 sm:flex sm:flex-nowrap justify-center items-center gap-2 md:gap-3 lg:gap-4 border border-gray-200 bg-white rounded-xl sm:rounded-full px-3 py-1 shadow-sm"
              role="tablist"
              aria-label="Learning stages"
            >
              {stages.map((stage, index) => (
                <CustomButton
                  key={stage.id}
                  variant={activeStage === index ? 'primary' : 'outline'}
                  size="sm"
                  onClick={() => setActiveStage(index)}
                  onKeyDown={(e) => handleKeyDown(e, index)}
                  role="tab"
                  aria-selected={activeStage === index}
                  aria-controls={`panel-${stage.id}`}
                  tabIndex={activeStage === index ? 0 : -1}
                  className={`
                    grid! grid-cols-[auto_1fr]! items-center! gap-2! md:gap-3! whitespace-nowrap!
                    px-4! md:px-5! py-1!
                    border-0! shadow-none!
                    transition-all duration-300 ease-in-out
                    ${activeStage === index ? 'scale-105!' : ''}
                  `}
                >
                  <span
                    className={`
                      flex items-center justify-center w-5 h-5 md:w-6 md:h-6 rounded-full
                      font-bold text-xs mr-2 md:mr-3
                      ${
                        activeStage === index
                          ? 'bg-white text-black'
                          : 'bg-ss-light-green text-black'
                      }
                    `}
                  >
                    {stage.id}
                  </span>
                  <span>{stage.title}</span>
                </CustomButton>
              ))}
            </nav>
          </div>
        </div>

        {/* Carousel Content */}
        <div className="relative">
          <AnimatePresence mode="wait">
            {stages.map((stage, index) => (
              activeStage === index && (
                <motion.div
                  key={stage.id}
                  id={`panel-${stage.id}`}
                  role="tabpanel"
                  aria-labelledby={`tab-${stage.id}`}
                  initial={{ opacity: 0 }}
                  animate={{ opacity: 1 }}
                  exit={{ opacity: 0 }}
                  transition={{ duration: 0.4, ease: 'easeInOut' }}
                  className="w-full"
                >
                  <div className="relative w-full aspect-video max-w-5xl mx-auto shadow-2xl overflow-hidden border border-gray-100">
                    <img 
                      src="/canvas_background.png" 
                      alt="Canvas background"
                      className="absolute inset-0 w-full h-full object-contain z-0"
                    />
                    <div className="absolute bottom-0 right-0 w-[85%] h-[90%] z-10">
                      {stage.videoSrc.endsWith('.mp4') ? (
                        <video
                          autoPlay
                          loop
                          muted
                          playsInline
                          className="w-full h-full object-contain"
                        >
                          <source src={stage.videoSrc} type="video/mp4" />
                          Your browser does not support the video tag.
                        </video>
                      ) : (
                        <img
                          src={stage.videoSrc}
                          alt={stage.alt}
                          className="w-full h-full object-contain"
                        />
                      )}
                    </div>
                  </div>
                </motion.div>
              )
            ))}
          </AnimatePresence>
        </div>

        {/* Progress Indicator Dots (Mobile-friendly alternative) */}
        <div className="flex justify-center gap-2 mt-8 md:hidden">
          {stages.map((_, index) => (
            <button
              key={index}
              onClick={() => setActiveStage(index)}
              aria-label={`Go to stage ${index + 1}`}
              className={`
                w-2 h-2 rounded-full transition-all duration-300
                ${activeStage === index ? 'bg-ss-light-green w-8' : 'bg-gray-300'}
              `}
            />
          ))}
        </div>
      </div>

    </div>
  );
};

export default LearningStagesCarousel;
